import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  CubeIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  DocumentTextIcon,
  UsersIcon,
  ChartBarIcon,
  CogIcon,
  ArrowLeftOnRectangleIcon,
  PlusIcon,
  EyeIcon,
  GiftIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

interface ModernSidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  showMobileSidebar: boolean;
  setShowMobileSidebar: (show: boolean) => void;
  isMobile: boolean;
}

const ModernSidebar: React.FC<ModernSidebarProps> = ({ 
  isCollapsed, 
  setIsCollapsed, 
  showMobileSidebar, 
  setShowMobileSidebar, 
  isMobile 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { icon: HomeIcon, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: CubeIcon, label: 'Packages', path: '/admin/packages' },
    { icon: BuildingOfficeIcon, label: 'Resorts', path: '/admin/resorts' },
    { icon: MapPinIcon, label: 'Destinations', path: '/admin/destinations' },
    { icon: DocumentTextIcon, label: 'Bookings', path: '/admin/bookings' },
    { icon: EyeIcon, label: 'Enquiries', path: '/admin/enquiries' },
    { icon: GiftIcon, label: 'Last Minute Deals', path: '/admin/last-minute-deals' },
    { icon: ChartBarIcon, label: 'Analytics', path: '/admin/analytics' },
    { icon: UsersIcon, label: 'Users', path: '/admin/users' },
    { icon: CogIcon, label: 'Settings', path: '/admin/settings' }
  ];

  // Quick Actions removed and moved to dashboard

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Don't render sidebar if mobile and not shown
  if (isMobile && !showMobileSidebar) {
    return null;
  }

  return (
    <div className={`fixed top-0 left-0 h-full bg-white shadow-lg border-r border-gray-200 z-50 transition-all duration-300 ${
      isMobile
        ? 'w-64' // Mobile: always full width when shown
        : isCollapsed
          ? 'w-16' // Desktop collapsed: narrow
          : 'w-64' // Desktop expanded: full width
    }`}>
      {/* Header */}
      <div className={`border-b border-gray-200 ${
        isCollapsed && !isMobile ? 'p-4' : 'p-6'
      }`}>
        <div className="flex items-center justify-between">
          <div className={`flex items-center ${
            isCollapsed && !isMobile ? 'justify-center' : 'space-x-3'
          }`}>
            <img src="/logo.png" alt="Bali Getaway" className="w-8 h-8 flex-shrink-0" />
            {(!isCollapsed || isMobile) && (
              <div>
                <h1 className="text-lg font-bold text-gray-900">Bali Getaway</h1>
                <p className="text-sm text-gray-500">Admin Panel</p>
              </div>
            )}
          </div>
          
          {/* Close button for mobile */}
          {isMobile && (
            <button
              onClick={() => setShowMobileSidebar(false)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 lg:hidden"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-gray-100">
        <div className={`flex items-center ${
          isCollapsed && !isMobile ? 'justify-center' : 'space-x-3'
        }`}>
          <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center font-bold text-white flex-shrink-0">
            {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
          </div>
          {(!isCollapsed || isMobile) && (
            <div>
              <p className="font-semibold text-gray-900 text-sm">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Navigation */}
      <div className="flex-1 overflow-y-auto">
        <nav className={`${
          isCollapsed && !isMobile ? 'p-2' : 'p-4'
        }`}>
          <div className="space-y-1">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => {
                  // Close mobile sidebar when navigating
                  if (isMobile) {
                    setShowMobileSidebar(false);
                  }
                }}
                className={`group flex items-center text-sm font-medium rounded-lg transition-colors ${
                  isActive(item.path)
                    ? isCollapsed && !isMobile
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-gradient-to-r from-yellow-50 to-orange-50 text-orange-700 border-l-4 border-orange-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                } ${
                  isCollapsed && !isMobile 
                    ? 'justify-center p-3 mx-1 w-12 h-12' 
                    : 'px-3 py-2.5'
                }`}
                title={isCollapsed && !isMobile ? item.label : ''}
              >
                <item.icon 
                  className={`h-5 w-5 flex-shrink-0 ${
                    isCollapsed && !isMobile ? '' : 'mr-3'
                  } ${
                    isActive(item.path) ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`} 
                />
                {(!isCollapsed || isMobile) && (
                  <span className="truncate">{item.label}</span>
                )}
              </Link>
            ))}
          </div>
        </nav>

        {/* Quick Actions removed - now in dashboard */}
      </div>

      {/* Footer */}
      <div className={`border-t border-gray-200 ${
        isCollapsed && !isMobile ? 'p-2' : 'p-4'
      }`}>
        <div className="space-y-1">
          <button
            onClick={handleLogout}
            className={`group flex items-center text-sm font-medium text-gray-600 rounded-lg hover:text-red-700 hover:bg-red-50 transition-colors ${
              isCollapsed && !isMobile 
                ? 'justify-center p-3 mx-1 w-12 h-12' 
                : 'w-full px-3 py-2'
            }`}
            title={isCollapsed && !isMobile ? 'Logout' : ''}
          >
            <ArrowLeftOnRectangleIcon className={`h-4 w-4 flex-shrink-0 ${
              isCollapsed && !isMobile ? '' : 'mr-3'
            } text-gray-400 group-hover:text-red-500`} />
            {(!isCollapsed || isMobile) && (
              <span className="truncate">Logout</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModernSidebar;
